class LiveAtomicHeader extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const { signal } = this.events;
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.menu = this.querySelector('[data-mobile-menu]');
    this.desktopMedia = window.matchMedia('(min-width: 768px)');

    this.toggle?.addEventListener('click', () => this.setMenu(!this.isOpen), { signal });
    this.addEventListener('click', (event) => {
      // Close before native anchor navigation or the cart drawer opens.
      if (this.isOpen && event.target.closest('a[href]')) this.setMenu(false);
    }, { signal });
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    this.desktopMedia.addEventListener('change', (event) => {
      if (event.matches) this.setMenu(false);
    }, { signal });
    this.setMenu(false);
    this.onScroll();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.setMenu(false);
  }

  get isOpen() {
    return this.toggle?.getAttribute('aria-expanded') === 'true';
  }

  setMenu(open) {
    if (!this.toggle || !this.menu) return;
    open = Boolean(open) && !this.desktopMedia.matches;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    this.menu.hidden = !open;
    this.classList.toggle('is-open', open);
    document.documentElement.classList.toggle('la-menu-open', open);
  }

  onScroll() {
    this.classList.toggle('is-stuck', window.scrollY > 24);
  }

  onKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      this.setMenu(false);
      this.toggle?.focus({ preventScroll: true });
    } else if (event.key === 'Tab') {
      const links = [...this.querySelectorAll('a[href], button:not([disabled])')]
        .filter((element) => element.getClientRects().length > 0);
      const first = links[0];
      const last = links[links.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  }
}

if (!customElements.get('live-atomic-header')) {
  customElements.define('live-atomic-header', LiveAtomicHeader);
}

class LiveAtomicOffer extends HTMLElement {
  connectedCallback() {
    this.radios = [...this.querySelectorAll('input[type="radio"]')];
    this.onChange = this.onChange.bind(this);
    this.radios.forEach((radio) => radio.addEventListener('change', this.onChange));
    this.update(this.querySelector('input[type="radio"]:checked')?.value || 'sub');
  }

  disconnectedCallback() {
    this.radios?.forEach((radio) => radio.removeEventListener('change', this.onChange));
  }

  onChange(event) {
    this.update(event.target.value);
  }

  update(plan) {
    const subscription = plan === 'sub';
    const price = subscription ? this.dataset.subPrice : this.dataset.onePrice;
    const daily = subscription ? this.dataset.subDaily : this.dataset.oneDaily;
    const dailyLabel = subscription ? this.dataset.subDailyLabel : this.dataset.oneDailyLabel;
    const sellingPlan = this.closest('form')?.querySelector('[data-offer-selling-plan]');

    const dailyValue = this.querySelector('[data-offer-daily]');
    const dailyDescription = this.querySelector('[data-offer-daily-label]');
    if (sellingPlan) sellingPlan.disabled = !subscription;
    this.querySelectorAll('[data-offer-main-price],[data-offer-atc-price]').forEach((node) => {
      node.textContent = price;
    });
    if (dailyValue) dailyValue.textContent = daily;
    if (dailyDescription) dailyDescription.textContent = dailyLabel;

    this.radios.forEach((radio) => {
      radio.closest('.la-buy-card__plan')?.classList.toggle('is-selected', radio.checked);
    });
  }
}

if (!customElements.get('live-atomic-offer')) {
  customElements.define('live-atomic-offer', LiveAtomicOffer);
}

class LiveAtomicProduct extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-plan-radio]').forEach((radio) => radio.addEventListener('change', () => this.update(radio.value)));
    this.update(this.querySelector('[data-plan-radio]:checked')?.value || 'one');
  }
  update(plan) {
    const quantity = this.querySelector('[data-product-quantity]');
    const sellingPlan = this.querySelector('[data-selling-plan]');
    const price = plan === 'sub'
      ? this.dataset.subPrice
      : plan === 'multi'
        ? this.dataset.multiPrice
        : this.dataset.onePrice;
    if (quantity) quantity.value = plan === 'multi' ? 3 : 1;
    if (sellingPlan) sellingPlan.disabled = plan !== 'sub';
    this.querySelectorAll('[data-product-price],[data-atc-price]').forEach((node) => node.textContent = price);
    this.querySelectorAll('.variant-option__label').forEach((label) => label.classList.toggle('is-selected', label.querySelector('input')?.checked));
  }
}
if (!customElements.get('live-atomic-product')) customElements.define('live-atomic-product', LiveAtomicProduct);

class LiveAtomicStudies extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-study-tab]').forEach((button) => {
      button.addEventListener('click', () => this.select(button.dataset.studyTab));
    });
  }
  select(key) {
    this.querySelectorAll('[data-study-tab]').forEach((button) => {
      button.setAttribute('aria-selected', String(button.dataset.studyTab === key));
    });
    this.querySelectorAll('[data-study-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.studyPanel !== key;
    });
  }
}
if (!customElements.get('live-atomic-studies')) customElements.define('live-atomic-studies', LiveAtomicStudies);

class LiveAtomicCapsule extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    const video = this.querySelector('video');
    const callouts = [...this.querySelectorAll('.v1-capsule-callout')];
    this.controller = new AbortController();
    this.timers = [];
    const { signal } = this.controller;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileVideo = video?.dataset.mobileSrc && window.matchMedia('(max-width: 640px)').matches;
    const fallback = this.querySelector('[data-capsule-fallback]');
    let revealed = false;
    let visible = false;
    let finished = false;
    const reveal = () => {
      if (revealed || signal.aborted) return;
      revealed = true;
      callouts.forEach((callout, index) => {
        this.timers.push(window.setTimeout(() => callout.classList.add('is-visible'), reducedMotion ? 0 : index * 150));
      });
    };
    if (!video) return reveal();
    this.video = video;
    video.muted = true;
    if (mobileVideo) {
      // Avoid alpha-video decoding/compositing on mobile. Preload is disabled
      // in markup so the desktop source is not fetched before this choice.
      video.src = video.dataset.mobileSrc;
      video.poster = video.dataset.mobilePoster;
    }
    const showFallback = () => {
      if (signal.aborted) return;
      if (fallback) {
        finished = true;
        video.pause();
        video.hidden = true;
        fallback.hidden = false;
      }
      reveal();
    };
    const play = () => {
      if (!visible || finished || reducedMotion || document.hidden || signal.aborted) return;
      video.play().catch((error) => {
        // Leaving the viewport can interrupt an outstanding play request.
        if (error.name !== 'AbortError') showFallback();
      });
    };
    video.addEventListener('ended', () => {
      finished = true;
      reveal();
    }, { signal });
    video.addEventListener('error', showFallback, { signal });

    if (reducedMotion) {
      if (fallback) return showFallback();
      video.preload = 'metadata';
      const showFinalFrame = () => {
        if (Number.isFinite(video.duration) && video.duration > 0) {
          video.currentTime = Math.max(0, video.duration - 0.04);
        }
      };
      if (video.readyState >= 1) showFinalFrame();
      else video.addEventListener('loadedmetadata', showFinalFrame, { once: true, signal });
      reveal();
      return;
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) video.pause();
      else play();
    }, { signal });
    this.observer = new IntersectionObserver((entries) => {
      // Start once half the stage is visible; pause only when fully offscreen.
      // Separate start/stop thresholds prevent scroll-edge stuttering.
      for (const entry of entries) {
        if (!entry.isIntersecting) visible = false;
        else if (entry.intersectionRatio >= 0.5) visible = true;
      }
      if (visible) play();
      else video.pause();
    }, { threshold: [0, 0.5] });
    this.observer.observe(this);
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.video?.pause();
    this.timers?.forEach((timer) => window.clearTimeout(timer));
  }
}
if (!customElements.get('live-atomic-capsule')) customElements.define('live-atomic-capsule', LiveAtomicCapsule);

class LiveAtomicIngredients extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-ingredient-drawer]');
    this.overlay = this.querySelector('.v1-ingredient-drawer-overlay');
    this.lastTrigger = null;
    this.onKeydown = this.onKeydown.bind(this);
    this.querySelectorAll('[data-ingredient-open]').forEach((button) => {
      button.addEventListener('click', () => this.open(button));
    });
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', this.onKeydown);
  }
  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.documentElement.classList.remove('ingredient-drawer-open');
  }
  open(button) {
    if (!this.drawer) return;
    const template = this.querySelector(`[data-ingredient-template="${button.dataset.ingredientOpen}"]`);
    if (!template) return;
    this.lastTrigger = button;
    this.drawer.replaceChildren(template.content.cloneNode(true));
    this.drawer.querySelector('[data-ingredient-close]')?.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-ingredient-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', String(trigger === button)));
    this.drawer.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ingredient-drawer-open');
    this.drawer.querySelector('[data-ingredient-close]')?.focus({ preventScroll: true });
  }
  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.querySelectorAll('[data-ingredient-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('ingredient-drawer-open');
    this.lastTrigger?.focus({ preventScroll: true });
  }
  onKeydown(event) {
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
  }
}
if (!customElements.get('live-atomic-ingredients')) customElements.define('live-atomic-ingredients', LiveAtomicIngredients);

class LiveAtomicTimeline extends HTMLElement {
  connectedCallback() {
    const steps = [...this.querySelectorAll('details')];
    if (!steps.some((details) => details.open) && steps[0]) steps[0].open = true;
    steps.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.querySelectorAll('details').forEach((sibling) => {
          if (sibling !== details) sibling.open = false;
        });
      });
    });
  }
}
if (!customElements.get('live-atomic-timeline')) customElements.define('live-atomic-timeline', LiveAtomicTimeline);

class LiveAtomicStickyAtc extends HTMLElement {
  connectedCallback() {
    const target = document.getElementById(this.dataset.buyTarget);
    if (!target || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      this.hidden = entry.isIntersecting;
    }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' });
    this.observer.observe(target);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
if (!customElements.get('live-atomic-sticky-atc')) customElements.define('live-atomic-sticky-atc', LiveAtomicStickyAtc);


document.addEventListener('click', (event) => {
  const thumb = event.target.closest('[data-gallery-thumb]');
  if (!thumb) return;
  const gallery = thumb.closest('[data-pdp-gallery]');
  const main = gallery?.querySelector('[data-gallery-main]');
  if (!main) return;
  main.src = thumb.dataset.src;
  main.alt = thumb.dataset.alt || '';
  const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
  thumbs.forEach((button) => {
    const active = button === thumb;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
  });
  const index = thumbs.indexOf(thumb);
  gallery.querySelectorAll('[data-gallery-dot]').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === index);
  });
  const railTrack = gallery.querySelector('[data-gallery-rail-track]');
  if (railTrack) railTrack.style.transform = `translateX(calc(${index + 1} * (-50% - 6px)))`;
});

document.addEventListener('click', (event) => {
  const dot = event.target.closest('[data-gallery-dot]');
  if (!dot) return;
  const gallery = dot.closest('[data-pdp-gallery]');
  const thumbs = gallery ? [...gallery.querySelectorAll('[data-gallery-thumb]')] : [];
  thumbs[Number(dot.dataset.galleryDot)]?.click();
});

const stepGallery = (gallery, delta) => {
  if (!gallery) return;
  const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
  if (!thumbs.length) return;
  const current = thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
  thumbs[(current + delta + thumbs.length) % thumbs.length].click();
};

document.addEventListener('click', (event) => {
  const nav = event.target.closest('[data-gallery-nav]');
  if (!nav) return;
  stepGallery(nav.closest('[data-pdp-gallery]'), Number(nav.dataset.galleryNav));
});

document.querySelectorAll('[data-gallery-stage]').forEach((stage) => {
  let start = null;
  stage.addEventListener('touchstart', (event) => {
    start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
  }, { passive: true });
  stage.addEventListener('touchend', (event) => {
    if (!start) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    start = null;
    if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
      stepGallery(stage.closest('[data-pdp-gallery]'), dx < 0 ? 1 : -1);
    }
  }, { passive: true });
});

if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.documentElement.classList.add('la-reveal-ready');
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: 0.12 });

  document.querySelectorAll('[data-reveal]').forEach((element) => revealObserver.observe(element));
}
